import { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowUp, X, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { ProjectChart } from './ProjectChart';
import { constructionProjectData } from '../data/projectData';
import { MermaidDiagram } from './MermaidDiagram';
import { chatService } from '../services/chatService';
import { MessageWithChart } from '../types/chat';

interface ChatbotProps {
  onClose: () => void;
  initialMessage?: string;
}

const WELCOME_PROMPTS = [
  'Where have you proven ROI from AI on infrastructure programmes?',
  'What guardrails sit around agents that can write back to live systems?',
  'How does your delivery model differ from a generic data consultancy?',
  'How do you scope and price a programme-scale AI rollout?',
];

export function Chatbot({ onClose, initialMessage }: ChatbotProps) {
  const [messages, setMessages] = useState<MessageWithChart[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const initialMessageProcessed = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const renderChart = (type: string, dataKey: keyof typeof constructionProjectData) => {
    const chartType = type as 'line' | 'bar' | 'pie' | 'radar';
    const chartData = constructionProjectData[dataKey];
    return chartData ? <ProjectChart type={chartType} data={chartData} title={dataKey} /> : null;
  };

  const processChartCommand = (content: string, isVisualRequest: boolean) => {
    let processedContent = content;
    let chart: JSX.Element | null = null;

    if (isVisualRequest && content.includes('```chart')) {
      const chartMatch = content.match(/```chart\n(.*?)\n```/s);
      if (chartMatch) {
        const chartConfig = chartMatch[1].split(',').map((s) => s.trim());
        const [type, dataKey] = chartConfig;
        const renderedChart = renderChart(type, dataKey as keyof typeof constructionProjectData);
        if (renderedChart) {
          chart = renderedChart;
        }
        processedContent = content.replace(/```chart\n.*?\n```/s, '');
      }
    }

    return { content: processedContent, chart };
  };

  useEffect(() => {
    if (initialMessage && !initialMessageProcessed.current) {
      void handleSubmitMessage(initialMessage);
      initialMessageProcessed.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessage]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  useEffect(() => {
    // close on ESC
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const renderMarkdown = useCallback((content: string) => {
    const components = {
      code({ inline, className, children, ...props }: any) {
        const match = /language-(\w+)/.exec(className || '');
        const value = String(children).replace(/\n$/, '');
        if (!inline && match?.[1] === 'mermaid') {
          return <MermaidDiagram chart={value} />;
        }
        return (
          <code className={className} {...props}>
            {children}
          </code>
        );
      },
    };
    return <ReactMarkdown components={components}>{content}</ReactMarkdown>;
  }, []);

  async function handleSubmitMessage(message: string) {
    if (!message.trim() || isLoading) return;

    const isVisualRequest = message.toLowerCase().trim().startsWith('visual');
    const userMessage = { role: 'user' as const, content: message };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await chatService.sendMessage([...messages, userMessage]);
      const { content, chart } = processChartCommand(response, isVisualRequest);
      setMessages((prev) => [...prev, { role: 'assistant', content, chart }]);
    } catch (error) {
      console.error('Error:', error);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' },
      ]);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await handleSubmitMessage(input);
  }

  function handleTextareaKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSubmitMessage(input);
    }
  }

  const hasMessages = messages.length > 0;

  return (
    <div className="fixed inset-0 z-50 bg-[#0A0F1E]/95 backdrop-blur-xl flex flex-col">
      {/* Top bar */}
      <div className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <span className="font-mono text-xs font-medium text-primary tracking-[0.16em] uppercase">
              Compass
            </span>
            <span className="text-white/30 text-xs">·</span>
            <span className="text-sm text-white/55">AI assistant</span>
          </div>
          <button
            onClick={onClose}
            aria-label="Close Compass"
            className="p-2 -mr-2 text-white/45 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Conversation column */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
          {!hasMessages && !isLoading && (
            <WelcomeState onPromptClick={(p) => void handleSubmitMessage(p)} />
          )}

          {hasMessages && (
            <div className="space-y-10">
              {messages.map((message, index) => (
                <MessageBlock key={index} message={message} renderMarkdown={renderMarkdown} />
              ))}
              {isLoading && (
                <div>
                  <p className="eyebrow text-primary mb-3">Compass</p>
                  <div className="flex items-center gap-3 text-white/55">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <span className="text-sm">Thinking</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>

      {/* Composer */}
      <div className="border-t border-white/10">
        <form onSubmit={handleSubmit} className="max-w-3xl mx-auto px-6 lg:px-8 py-5">
          <div className="flex items-end gap-3">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleTextareaKey}
              placeholder="Ask Compass anything about Movar Reply, programmes, or AI agents."
              rows={1}
              className="flex-1 bg-transparent border-none resize-none text-white placeholder:text-white/35 focus:outline-none text-base leading-snug py-2 max-h-40 overflow-y-auto"
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={isLoading || !input.trim()}
              aria-label="Send"
              className="shrink-0 w-9 h-9 flex items-center justify-center bg-primary text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#0066E0] transition-colors"
            >
              <ArrowUp className="w-4 h-4" strokeWidth={2.5} />
            </button>
          </div>
          <p className="mt-3 font-mono text-[10px] tracking-[0.14em] uppercase text-white/30">
            Enter to send · Shift + Enter for newline · Esc to close
          </p>
        </form>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */

function WelcomeState({ onPromptClick }: { onPromptClick: (prompt: string) => void }) {
  return (
    <div>
      <p className="eyebrow text-primary mb-6">Compass</p>
      <h2 className="display-md text-white mb-5 max-w-2xl">
        Your AI PMO consultant. Ready when you are.
      </h2>
      <p className="text-base md:text-lg text-white/65 max-w-2xl leading-snug mb-12">
        Ask anything about Movar Reply&apos;s services, our delivery approach, AI agents on live
        programmes, or how an engagement would actually run with your team.
      </p>

      <p className="eyebrow text-white/45 mb-4">Try</p>
      <ul className="space-y-px">
        {WELCOME_PROMPTS.map((prompt, i) => (
          <li key={prompt}>
            <button
              onClick={() => onPromptClick(prompt)}
              className="group w-full text-left flex items-start gap-4 py-5 border-t border-white/10 last:border-b hover:border-primary transition-colors"
            >
              <span className="eyebrow text-white/40 pt-1 shrink-0">
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className="flex-1 text-base md:text-lg text-white/85 group-hover:text-white transition-colors leading-snug">
                {prompt}
              </span>
              <span className="font-mono text-xs text-white/30 group-hover:text-primary mt-1.5 shrink-0 transition-colors">
                →
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface MessageBlockProps {
  message: MessageWithChart;
  renderMarkdown: (content: string) => JSX.Element;
}

function MessageBlock({ message, renderMarkdown }: MessageBlockProps) {
  const isUser = message.role === 'user';
  return (
    <div>
      <p
        className={`eyebrow mb-3 ${
          isUser ? 'text-white/55' : 'text-primary'
        }`}
      >
        {isUser ? 'You' : 'Compass'}
      </p>
      <div
        className={`prose prose-invert max-w-none leading-relaxed ${
          isUser ? 'text-white' : 'text-white/85'
        }`}
      >
        {renderMarkdown(message.content)}
      </div>
      {message.chart && <div className="mt-6">{message.chart}</div>}
    </div>
  );
}
